import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import moment from "moment";
import { useTaskContext } from "../context/TaskContext";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import Avatar from "./Avatar";
import { formatShortName } from "../hooks/useUserAvatar";
import { tagStyles } from "../hooks/useAddProductConfig";

// ===== Estilos base =====
const defaultModalStyles = {
  overlay: {
    backgroundColor: "rgba(15,23,42,.55)",
    zIndex: 1008,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  content: {
    inset: "auto",
    width: "450px",
    maxHeight: "85vh",
    overflow: "auto",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0 20px 40px rgba(2,6,23,.18)",
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": { width: "0px", background: "transparent" },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  label: { fontSize: 14, color: "#64748b", display: "block", marginTop: 4 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#F8FAFB",
    outline: "none",
    margin: "6px 0 12px",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#F8FAFB",
    fontFamily: "Inter, sans-serif",
    outline: "none",
    margin: "6px 0 12px",
    minHeight: 86,
    resize: "vertical",
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    background: "#F8FAFB",
  },
  row3: { display: "grid", gridTemplateColumns: "1fr 96px 1fr", gap: 12 },
  inputGroup: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#F8FAFB",
    padding: "0 12px",
    height: 44,
    margin: "6px 0 12px",
  },
  inputGroupField: {
    border: "none",
    outline: "none",
    background: "transparent",
    flex: 1,
    height: 40,
    padding: 0,
    font: "inherit",
  },
  select: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#F8FAFB",
    outline: "none",
    margin: "6px 0 12px",
    appearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%2394a3b8' d='M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    backgroundSize: "16px",
  },
  button: {
    padding: "12px 18px",
    border: 0,
    borderRadius: "4px",
    background: "#3B82F6",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  chipStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#fff",
    margin: "6px 0 12px",
    fontWeight: 600,
    color: "#0f172a",
  },
  dot: (color) => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: color || "#6C5CE7",
  }),
  // Usuarios
  usersRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "6px 0 12px",
    flexWrap: "nowrap",
  },
  avatarWrap: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid #fff",
  },
  moreChip: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: -18, // se pega igual que los avatares
    border: "2px solid #fff", // anillo blanco como los avatares
    background: "#3B82F6", // azul
    color: "#fff",
    fontWeight: 800,
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 28px",
  },
  inviteBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 600,
    cursor: "pointer",
  },
  inviteDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 6,
    width: 260,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    boxShadow: "0 20px 40px rgba(2,6,23,.12)",
    zIndex: 10,
    padding: 8,
  },
  inviteItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    border: 0,
    background: "transparent",
    borderRadius: 8,
    cursor: "pointer",
  },
  dropdownEmpty: { fontSize: 13, color: "#94a3b8", padding: "8px 10px" },
  tagsBox: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "6px 8px",
    borderRadius: "10px",
    border: "1px solid #E5E7EB",
    background: "#F8FAFB",
    margin: "6px 0 12px",
  },
  tagChip: (i) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 28,
    padding: "0 10px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    boxShadow: "0 2px 4px rgba(0,0,0,.04)",
  }),
  tagClose: {
    cursor: "pointer",
    opacity: 0.7,
  },
  tagInput: {
    flex: 1,
    minWidth: 120,
    height: 28,
    border: "none",
    outline: "none",
    background: "transparent",
    padding: "0 6px",
  },
};

const CreateTaskModal = ({
  isOpen,
  onRequestClose,
  taskToEdit,
  listName,
  projectId,
  listId,
  ownerUid,
  customStyles, // opcional
  usuarios = [],
}) => {
  const { firstName, lastName, avatarUrl, user: authUser } = useAuth();
  const { createTask, updateTask } = useTaskContext();

  // helper de estilos
  const sx = (k) => ({
    ...defaultModalStyles[k],
    ...(customStyles?.[k] || {}),
  });
  const modalStyle = { overlay: sx("overlay"), content: sx("content") };

  // ===== State =====
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [dateStart, setDateStart] = useState(moment().format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(
    moment().add(1, "day").format("YYYY-MM-DD")
  );
  const [user, setUser] = useState(`${firstName} ${lastName}`);
  const [tags, setTags] = useState([]);
  const [tagValue, setTagValue] = useState(""); // <— valor del input de etiquetas
  const [taggedUsers, setTaggedUsers] = useState([
    {
      id: authUser?.uid,
      nombre: firstName,
      apellido: lastName,
      email: authUser?.email,
      avatarUrl,
    },
  ]);
  const [category, setCategory] = useState(listName || "Por Hacer");
  const [notifType, setNotifType] = useState("Alerta");
  const [notifQty, setNotifQty] = useState(1);
  const [notifUnit, setNotifUnit] = useState("Días");
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteRef = useRef(null);
  const isEditing = !!taskToEdit;

  // cerrar dropdown al hacer click fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (inviteRef.current && !inviteRef.current.contains(e.target))
        setInviteOpen(false);
    };
    if (inviteOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [inviteOpen]);

  // cargar datos si edita / reset si crea
  useEffect(() => {
    const creator = {
      id: authUser?.uid,
      nombre: firstName,
      apellido: lastName,
      email: authUser?.email,
      avatarUrl,
    };

    if (isEditing && taskToEdit) {
      setTitle(taskToEdit.title || "");
      setNote(taskToEdit.note || "");
      setDateStart(taskToEdit.dateStart || moment().format("YYYY-MM-DD"));
      setDateEnd(taskToEdit.dateEnd || "");
      setUser(taskToEdit.user || `${firstName} ${lastName}`);
      setTags(taskToEdit.tags || []);
      setTaggedUsers(
        taskToEdit.taggedUsers && taskToEdit.taggedUsers.length > 0
          ? taskToEdit.taggedUsers
          : [creator]
      );
      setCategory(taskToEdit.status || listName || "Por Hacer");
      setNotifType(taskToEdit.notifType || "Alerta");
      setNotifQty(
        typeof taskToEdit.notifQty === "number" ? taskToEdit.notifQty : 1
      );
      setNotifUnit(taskToEdit.notifUnit || "Días");
      setTagValue("");
    } else {
      setTitle("");
      setNote("");
      setDateStart(moment().format("YYYY-MM-DD"));
      setDateEnd(""); // sin fecha término por defecto
      setUser(`${firstName} ${lastName}`);
      setTags([]);
      setTaggedUsers([creator]);
      setCategory(listName || "Por Hacer");
      setNotifType("Alerta");
      setNotifQty(1);
      setNotifUnit("Días");
      setTagValue("");
    }
  }, [
    isEditing,
    taskToEdit,
    firstName,
    lastName,
    authUser?.uid,
    authUser?.email,
    avatarUrl,
    listName,
  ]);

  // === Usuarios (invitar) ===
  const addUserById = (id) => {
    if (!id) return;
    if (!taggedUsers.some((u) => u.id === id)) {
      const found = usuarios.find((u) => u.id === id);
      if (found) setTaggedUsers((prev) => [...prev, found]);
    }
    setInviteOpen(false);
  };
  const getAvatarItemStyle = (i) => ({
    ...sx("avatarWrap"),
    marginLeft: i ? -8 : 0,
    zIndex: 10 - i,
  });

  // === Etiquetas ===
  const addTag = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (tags.includes(v)) return;
    if (tags.length >= 5) return;
    setTags((t) => [...t, v]);
  };

  const onTagKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagValue.trim() !== "") {
      e.preventDefault();
      addTag(tagValue);
      setTagValue("");
    } else if (e.key === "Backspace" && tagValue === "" && tags.length) {
      // borrar el último chip si el input está vacío
      setTags((t) => t.slice(0, -1));
    }
  };

  const removeTag = (idx) => setTags((t) => t.filter((_, i) => i !== idx));

  // === Guardar ===
  const handleSave = async () => {
    if (!title.trim()) {
      return Swal.fire("Error", "El título no puede estar vacío", "error");
    }
    if (!projectId && !listId) {
      return Swal.fire(
        "Error",
        "Falta referencia de proyecto o lista",
        "error"
      );
    }

    // ids de participantes (si no hay seleccionados, solo el creador)
    const participantIds = (
      taggedUsers?.length ? taggedUsers : [{ id: authUser?.uid }]
    )
      .map((u) => u?.id)
      .filter(Boolean);

    const taskData = {
      projectId: projectId ?? null,
      listId: listId ?? null,
      isFast: !projectId,
      status: category,
      title: title.trim(),
      note: (note || "").trim(),
      dateStart,
      dateEnd: dateEnd || null,
      user: user || `${firstName} ${lastName}`,
      userName: user || `${firstName} ${lastName}`,
      userId: authUser?.uid,
      avatarUrl,
      tags,
      taggedUsers, // objetos completos para el modal
      participants: [...new Set(participantIds)], // ids para mostrar en el board
      notifType,
      notifQty,
      notifUnit,
      comments: taskToEdit?.comments || [],
      attachments: taskToEdit?.attachments || [],
    };

    if (isEditing) {
      await updateTask(taskToEdit.id, taskData);
    } else {
      await createTask(taskData);
    }
    onRequestClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={modalStyle}
      contentLabel={isEditing ? "Editar Tarea" : "Agregar Tarea"}
    >
      {/* Header */}
      <div
        style={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2 style={{ color: "#44566C" }}>
          {isEditing ? "Editar Tarea" : "Agregar Tarea"}
        </h2>
        <button
          onClick={onRequestClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "#EAEDF0",
            color: "#44566C",
            borderRadius: "50%",
            height: 32,
            width: 32,
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            lineHeight: 0,
            fontSize: 16,
          }}
          aria-label="Cerrar modal"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Título */}
      <label style={sx("label")}>Título</label>
      <input
        type="text"
        style={sx("input")}
        placeholder="Agrega un título..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {/* Nota */}
      <label style={sx("label")}>Nota</label>
      <textarea
        style={sx("textarea")}
        placeholder="Agrega una nota..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {/* Usuarios */}
      <label style={sx("label")}>Usuarios</label>
      <div ref={inviteRef} style={sx("usersRow")}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {taggedUsers.slice(0, 3).map((u, i) => (
            <div key={u.id || i} style={getAvatarItemStyle(i)}>
              <Avatar userId={u.id} size={28} />
            </div>
          ))}
        </div>
        {taggedUsers.length > 3 && (
          <span style={sx("moreChip")}>+{taggedUsers.length - 3}</span>
        )}
        <button
          type="button"
          style={sx("inviteBtn")}
          onClick={() => setInviteOpen((v) => !v)}
        >
          <span style={{ fontWeight: 800, lineHeight: 1 }}>+</span> Invitar
        </button>
        {inviteOpen && (
          <div style={sx("inviteDropdown")}>
            {usuarios.filter((u) => !taggedUsers.some((t) => t.id === u.id))
              .length === 0 ? (
              <div style={sx("dropdownEmpty")}>Todos asignados</div>
            ) : (
              usuarios
                .filter((u) => !taggedUsers.some((t) => t.id === u.id))
                .map((u) => {
                  return (
                    <button
                      key={u.id}
                      type="button"
                      style={sx("inviteItem")}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#F8FAFC")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                      onClick={() => addUserById(u.id)}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <Avatar userId={u.id} size={24} />
                      </div>
                      <span style={{ fontSize: 14, color: "#0f172a" }}>
                        {formatShortName(u)}
                      </span>
                    </button>
                  );
                })
            )}
          </div>
        )}
      </div>

      {/* Etiquetas */}
      <label style={sx("label")}>Etiquetas</label>
      <div style={tagStyles.tagsBox}>
        {tags.map((tag, i) => (
          <div key={`${tag}-${i}`} style={tagStyles.tagChip(i)}>
            {tag}
            <span style={tagStyles.tagClose} onClick={() => removeTag(i)}>
              <i className="bi bi-x"></i>
            </span>
          </div>
        ))}
        {tags.length < 5 && (
          <input
            style={tagStyles.tagInput}
            placeholder="Escribe un tag y presiona Enter..."
            value={tagValue}
            onChange={(e) => setTagValue(e.target.value)}
            onKeyDown={onTagKeyDown}
          />
        )}
      </div>

      {/* Fecha Inicio */}
      <label style={sx("label")}>Fecha Inicio</label>
      <div style={sx("inputGroup")}>
        <span style={{ marginRight: 8, color: "#94a3b8", fontSize: 16 }}>
          <i className="bi bi-calendar2-week" />
        </span>
        <input
          type="date"
          value={dateStart}
          onChange={(e) => setDateStart(e.target.value)}
          style={sx("inputGroupField")}
        />
      </div>

      {/* Notificación */}
      <label style={sx("label")}>Notificación</label>
      <div style={sx("row3")}>
        <select
          value={notifType}
          onChange={(e) => setNotifType(e.target.value)}
          style={sx("select")}
        >
          <option>Alerta</option>
          <option>Correo</option>
          <option>Push</option>
        </select>
        <input
          type="number"
          min={1}
          value={notifQty}
          onChange={(e) => setNotifQty(+e.target.value || 1)}
          style={sx("input")}
        />
        <select
          value={notifUnit}
          onChange={(e) => setNotifUnit(e.target.value)}
          style={sx("select")}
        >
          <option>Días</option>
          <option>Horas</option>
          <option>Minutos</option>
        </select>
      </div>

      {/* Responsable */}
      <label style={sx("label")}>Usuario</label>
      <input
        type="text"
        placeholder="Agrega el usuario"
        style={sx("input")}
        value={user}
        onChange={(e) => setUser(e.target.value)}
      />

      {/* Guardar */}
      <div style={{ textAlign: "left", marginTop: 8 }}>
        <button style={sx("button")} onClick={handleSave}>
          {isEditing ? "Actualizar" : "Guardar"}
        </button>
      </div>
    </Modal>
  );
};

export default CreateTaskModal;
